/**
 * Drifting dots across the hero — a canvas layer between the film still and
 * the copy.
 *
 * Canvas rather than DOM nodes: a hundred-odd moving dots as elements would
 * re-layout every frame. Dots take the palette's hero accent (re-read every
 * second so a palette switch follows), mixed with soft white.
 *
 * `avoidRef` is the mascot: dots fade out as they near it and never cross it,
 * so the robot head stays clean.
 *
 * Costs nothing when unseen — the loop stops while the hero is scrolled away
 * or the tab is hidden — and reduced-motion visitors get the same dots, still.
 */

import React, { useEffect, useRef } from 'react';

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** Accent dot (true) or soft white (false). */
  accent: boolean;
  /** Base opacity, before the mascot fade and twinkle. */
  a: number;
  /** Twinkle phase. */
  t: number;
}

/**
 * One dot per this many square pixels, capped so a 4K screen stays light. A
 * phone's hero is small enough that density alone leaves it sparse, so there
 * is a floor too.
 */
const AREA_PER_DOT = 7000;
const MIN_DOTS = 50;
const MAX_DOTS = 170;
/** Clear space kept around the mascot, beyond its own radius, in px. */
const AVOID_PAD = 28;
/** Width of the fade band outside that clear space, in px. */
const FADE = 60;

export const ElfHeroDots: React.FC<{ avoidRef: React.RefObject<HTMLElement> }> = ({ avoidRef }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    // Reduced-motion visitors get the same field, drifting slowly, rather than a
    // frozen one: the dots are a texture, and stopping them read as broken.
    const gentle = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const pace = gentle ? 0.35 : 1;

    let dots: Dot[] = [];
    let w = 0;
    let h = 0;
    let accent = '235, 144, 39';
    let raf = 0;
    let visible = true;
    let last = performance.now();

    const readAccent = () => {
      const v = getComputedStyle(canvas).getPropertyValue('--acc-hero-rgb').trim();
      if (v) accent = v.replace(/\s+/g, ' ').split(/[ ,]+/).join(', ');
    };

    const seed = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(MIN_DOTS, Math.min(MAX_DOTS, Math.round((w * h) / AREA_PER_DOT)));
      dots = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 14,
        vy: -(6 + Math.random() * 16),
        r: 1.4 + Math.random() * 2.2,
        accent: Math.random() < 0.4,
        a: 0.5 + Math.random() * 0.45,
        t: Math.random() * Math.PI * 2,
      }));
    };

    /** Mascot circle in canvas coordinates, or null if it isn't on screen. */
    const avoid = () => {
      const el = avoidRef.current;
      if (!el) return null;
      const m = el.getBoundingClientRect();
      if (!m.width) return null;
      const c = canvas.getBoundingClientRect();
      return { cx: m.left - c.left + m.width / 2, cy: m.top - c.top + m.height / 2, r: Math.max(m.width, m.height) / 2 + AVOID_PAD };
    };

    const draw = (dt: number) => {
      ctx.clearRect(0, 0, w, h);
      const hole = avoid();
      for (const d of dots) {
        d.x += d.vx * dt * pace;
        d.y += d.vy * dt * pace;
        d.t += dt * 1.6 * pace;
        if (d.y < -12) { d.y = h + 12; d.x = Math.random() * w; }
        if (d.x < -12) d.x = w + 12;
        else if (d.x > w + 12) d.x = -12;

        let fade = 1;
        if (hole) {
          const dist = Math.hypot(d.x - hole.cx, d.y - hole.cy);
          if (dist < hole.r) continue;
          if (dist < hole.r + FADE) fade = (dist - hole.r) / FADE;
        }
        const alpha = d.a * fade * (0.75 + 0.25 * Math.sin(d.t));
        const rgb = d.accent ? accent : '255, 255, 255';
        // Soft halo first, then the core — reads as a glowing point against the
        // photograph without the per-dot cost of canvas shadowBlur.
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r * 2.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${alpha * 0.16})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${alpha})`;
        ctx.fill();
      }
    };

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      draw(dt);
      raf = visible && !document.hidden ? requestAnimationFrame(tick) : 0;
    };
    const start = () => {
      if (raf || !visible || document.hidden) return;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };

    readAccent();
    seed();
    draw(0);
    start();

    const ro = new ResizeObserver(() => { seed(); draw(0); });
    ro.observe(canvas);
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      start();
    });
    io.observe(canvas);
    const onVisibility = () => start();
    document.addEventListener('visibilitychange', onVisibility);
    const accentTimer = window.setInterval(readAccent, 1000);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(accentTimer);
    };
  }, [avoidRef]);

  return <canvas ref={canvasRef} className="elf-hero__dots" aria-hidden="true" />;
};

export default ElfHeroDots;
